import { useState } from "react";
import { Check, Database, RefreshCw, RotateCcw, ShieldCheck, Trash2, Users, X } from "lucide-react";
import { ApiError, apiFetch } from "./services/http";
import "./admin.css";

type ModerationCase = {
  id: string;
  target_type: "card" | "comment" | "post";
  target_id: string;
  reason: string;
  created_at: string;
  content?: { author?: string; text?: string; title?: string; body?: string; imageUrl?: string; section?: string };
};
type UserRow = { id: string; nickname: string; createdAt: string; cards: number; posts: number; comments: number; followers: number };
type CleanupJob = {
  id: string;
  objectPath: string;
  status: "pending" | "failed";
  attempts: number;
  lastError?: string;
  nextAttemptAt: string;
  createdAt: string;
  updatedAt: string;
};
type DeletionLog = {
  id: string;
  actorUserId?: string;
  targetType: "account" | "card" | "comment" | "post";
  targetId: string;
  objectCount: number;
  cleanupPendingCount: number;
  createdAt: string;
};

const localKey = import.meta.env.DEV ? "local-admin-123456" : "";
const targetLabels: Record<string, string> = { account: "账号", card: "动物卡", comment: "评论", post: "帖子" };

export default function AdminApp() {
  const [key, setKey] = useState(localKey);
  const [cases, setCases] = useState<ModerationCase[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [cleanupJobs, setCleanupJobs] = useState<CleanupJob[]>([]);
  const [deletionLogs, setDeletionLogs] = useState<DeletionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("输入运营密钥后加载后台数据。");
  const headers = { "x-admin-key": key };

  const load = async () => {
    setLoading(true);
    try {
      const [moderation, userData, cleanupData, deletionData] = await Promise.all([
        apiFetch<{ cases: ModerationCase[] }>("/api/v1/admin/moderation", { headers }),
        apiFetch<{ users: UserRow[] }>("/api/v1/admin/users", { headers }),
        apiFetch<{ jobs: CleanupJob[] }>("/api/v1/admin/storage-cleanup-jobs", { headers }),
        apiFetch<{ logs: DeletionLog[] }>("/api/v1/admin/deletion-logs", { headers }),
      ]);
      setCases(moderation.cases);
      setUsers(userData.users);
      setCleanupJobs(cleanupData.jobs);
      setDeletionLogs(deletionData.logs);
      setNotice("数据已更新");
    } catch (caught) {
      setNotice(caught instanceof ApiError && caught.status === 401 ? "运营密钥无效，无法查看后台数据。" : "加载失败，请确认后端已启动。");
    } finally { setLoading(false); }
  };

  const decide = async (caseId: string, decision: "published" | "hidden") => {
    const reason = decision === "hidden" ? window.prompt("请输入未通过原因（用户将看到这段说明）") : undefined;
    if (decision === "hidden" && !reason?.trim()) return;
    try {
      await apiFetch(`/api/v1/admin/moderation/${caseId}`, { method: "POST", headers: { ...headers, "content-type": "application/json" }, body: JSON.stringify({ decision, reason: reason?.trim() }) });
      setCases((items) => items.filter((item) => item.id !== caseId));
      setNotice(decision === "published" ? "内容已通过并公开。" : "内容已隐藏并保留处置记录。");
    } catch { setNotice("操作失败，请重新加载后重试。"); }
  };

  const retryCleanup = async (jobId: string) => {
    try {
      await apiFetch(`/api/v1/admin/storage-cleanup-jobs/${jobId}/retry`, { method: "POST", headers });
      setCleanupJobs((items) => items.map((item) => item.id === jobId ? { ...item, status: "pending", attempts: 0, lastError: undefined, nextAttemptAt: new Date().toISOString() } : item));
      setNotice("已重新提交图片清理任务，稍后刷新可查看结果。");
    } catch { setNotice("重试失败，请确认服务状态后再试。"); }
  };

  const pendingCleanup = cleanupJobs.filter((job) => job.status === "pending").length;
  const failedCleanup = cleanupJobs.filter((job) => job.status === "failed").length;

  return (
    <main className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-brand"><span>MA</span><div><strong>城市动物图鉴</strong><small>运营后台</small></div></div>
        <nav>
          <button className="active"><ShieldCheck size={18} />内容审核</button>
          <button><Users size={18} />用户档案</button>
          <button><Database size={18} />图片清理</button>
          <button><Trash2 size={18} />删除记录</button>
        </nav>
        <p>后台记录内容处置与图片清理结果。<br />生产环境请使用独立运营密钥。</p>
      </aside>
      <section className="admin-main">
        <header className="admin-header"><div><p>OPERATIONS CONSOLE</p><h1>内容安全与社区运营</h1></div><button onClick={load} disabled={loading}><RefreshCw size={17} className={loading ? "spin" : ""} />刷新数据</button></header>
        <section className="admin-access"><label>运营密钥<input value={key} type="password" onChange={(event) => setKey(event.target.value)} placeholder="请输入后台密钥" /></label><button onClick={load} disabled={!key || loading}>进入后台</button><small>{notice}</small></section>
        <section className="admin-stats">
          <article><strong>{cases.length}</strong><span>待审核内容</span></article>
          <article><strong>{users.length}</strong><span>已注册用户</span></article>
          <article><strong>{pendingCleanup}</strong><span>待清理图片</span></article>
          <article><strong>{failedCleanup}</strong><span>需人工重试</span></article>
        </section>

        <section className="admin-panel"><div className="panel-heading"><div><p>MODERATION QUEUE</p><h2>待审核内容</h2></div><span>{cases.length} 项</span></div>{cases.length ? <div className="moderation-list">{cases.map((item) => <article key={item.id} className="moderation-item"><div className="moderation-kind">{item.target_type === "post" ? "帖子" : item.target_type === "card" ? "动物卡" : "回复"}</div><div className="moderation-copy"><strong>{item.content?.title || item.content?.author || "待审核用户内容"}</strong><p>{item.content?.body || item.content?.text || "图片内容待人工复核"}</p><small>{item.reason} · {new Date(item.created_at).toLocaleString("zh-CN")}</small></div><div className="moderation-actions"><button onClick={() => decide(item.id, "published")}><Check size={16} />通过</button><button className="danger" onClick={() => decide(item.id, "hidden")}><X size={16} />隐藏</button></div></article>)}</div> : <div className="admin-empty"><ShieldCheck size={27} /><strong>暂无待审核内容</strong><span>新发布的动物卡、图文帖子和回复会在这里出现。</span></div>}</section>

        <section className="admin-panel"><div className="panel-heading"><div><p>STORAGE CLEANUP</p><h2>图片清理队列</h2></div><span>{cleanupJobs.length} 项</span></div>{cleanupJobs.length ? <div className="cleanup-table"><div className="cleanup-head"><span>对象路径</span><span>状态</span><span>执行信息</span><span>操作</span></div>{cleanupJobs.map((job) => <div className="cleanup-row" key={job.id}><span title={job.objectPath}>{job.objectPath}</span><span><b className={`cleanup-status ${job.status}`}>{job.status === "failed" ? "重试失败" : "等待清理"}</b></span><span><strong>已尝试 {job.attempts} 次</strong><small>{job.lastError || `下次执行：${new Date(job.nextAttemptAt).toLocaleString("zh-CN")}`}</small></span><span><button onClick={() => retryCleanup(job.id)}><RotateCcw size={14} />立即重试</button></span></div>)}</div> : <div className="admin-empty"><Database size={27} /><strong>图片清理队列为空</strong><span>删除内容时产生的 COS 清理失败会自动进入这里。</span></div>}</section>

        <section className="admin-panel"><div className="panel-heading"><div><p>DELETION AUDIT</p><h2>删除记录</h2></div><span>{deletionLogs.length} 条</span></div>{deletionLogs.length ? <div className="audit-table"><div className="audit-head"><span>删除对象</span><span>对象编号</span><span>图片处理</span><span>时间</span></div>{deletionLogs.map((log) => <div className="audit-row" key={log.id}><span><b>{targetLabels[log.targetType] || log.targetType}</b><small>{log.actorUserId ? `操作者 ${log.actorUserId}` : "系统操作"}</small></span><span title={log.targetId}>{log.targetId}</span><span>{log.objectCount} 张{log.cleanupPendingCount ? ` · ${log.cleanupPendingCount} 张待清理` : " · 已完成"}</span><span>{new Date(log.createdAt).toLocaleString("zh-CN")}</span></div>)}</div> : <div className="admin-empty"><Trash2 size={27} /><strong>暂无删除记录</strong><span>删除账号、动物卡、帖子或评论后会生成审计记录。</span></div>}</section>

        <section className="admin-panel"><div className="panel-heading"><div><p>USER DIRECTORY</p><h2>用户概览</h2></div><span>{users.length} 人</span></div><div className="user-table"><div className="table-head"><span>用户</span><span>内容</span><span>粉丝</span><span>注册时间</span></div>{users.map((user) => <div className="table-row" key={user.id}><span><b>{user.nickname.slice(0, 1)}</b><strong>{user.nickname}</strong></span><span>{user.cards} 卡 · {user.posts} 帖 · {user.comments} 评</span><span>{user.followers}</span><span>{new Date(user.createdAt).toLocaleDateString("zh-CN")}</span></div>)}</div></section>
      </section>
    </main>
  );
}
